import React from 'react';
import { Menu } from '@headlessui/react';
import {
  Bars3Icon,
  ArrowPathIcon,
  UserPlusIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

interface ActionBarProps {
  refreshing?: boolean;
  canAddTenancy?: boolean;
  canComposeBroadcast?: boolean;
  onRefresh: () => void;
  onAddTenancy: () => void;
  onComposeBroadcast: () => void;
}

const ActionBar: React.FC<ActionBarProps> = ({
  refreshing = false,
  canAddTenancy = false,
  canComposeBroadcast = false,
  onRefresh,
  onAddTenancy,
  onComposeBroadcast
}) => {
  return (
    <div className="flex items-center justify-end gap-4 mb-8">
      {/* --- DESKTOP VIEW: Visible Buttons (Hidden on Mobile) --- */}
      <div className="hidden sm:flex items-center gap-3">
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
        >
          <ArrowPathIcon
            className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>

        {canAddTenancy && (
          <button
            onClick={onAddTenancy}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Tenancy
          </button>
        )}

        {canComposeBroadcast && (
          <button
            onClick={onComposeBroadcast}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
          >
            <EnvelopeIcon className="h-4 w-4 text-gray-500" />
            Message
          </button>
        )}
      </div>

      {/* --- MOBILE VIEW: Dropdown Menu (Hidden on Desktop) --- */}
      <div className="sm:hidden">
        <Menu as="div" className="relative inline-block text-left">
          {/* Menu Button */}
          <Menu.Button className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <span className="sr-only">Open options</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </Menu.Button>

          {/* Menu Items */}
          <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={onRefresh}
                    disabled={refreshing}
                    className={`${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <ArrowPathIcon
                      className={`mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 ${
                        refreshing ? 'animate-spin' : ''
                      }`}
                      aria-hidden="true"
                    />
                    Refresh
                  </button>
                )}
              </Menu.Item>
            </div>

            {(canAddTenancy || canComposeBroadcast) && (
              <div className="py-1">
                {canAddTenancy && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={onAddTenancy}
                        className={`${
                          active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        } group flex w-full items-center px-4 py-2 text-sm`}
                      >
                        <UserPlusIcon
                          className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                        Add Tenancy
                      </button>
                    )}
                  </Menu.Item>
                )}
                {canComposeBroadcast && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={onComposeBroadcast}
                        className={`${
                          active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        } group flex w-full items-center px-4 py-2 text-sm`}
                      >
                        <EnvelopeIcon
                          className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                        Message
                      </button>
                    )}
                  </Menu.Item>
                )}
              </div>
            )}
          </Menu.Items>
        </Menu>
      </div>
    </div>
  );
};

export default ActionBar;
